// src/pdf-calcs/pdf-calcs-atm.js

import {
   calculateAtmSemiTrailer,
   calculateAtmRoadTrain,
   calculateAtmBDouble,
   calculateAtmLowLoader,
   calculateAtmDogTrailer,
} from './atm-calculations.js';
import { readNumeric, normalizeValue, formatFieldValue, formatResult } from './pdf-calcs-utils.js';

// --- Semi Trailer ---

const ST_DEFAULTS = {
   stSteerAxleMass: 11.0,
   stDriveAxleGroupMass: 17.5,
   stTrailerAxleGroupMass: 23.5,
   stPrimeMoverTare: 8.5,
};

const ST_FIELD_RULES = {
   stSteerAxleMass: { min: 0, precision: 3, warningMax: 200 },
   stDriveAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   stTrailerAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   stPrimeMoverTare: { min: 0, precision: 3, warningMax: 200 },
};

// --- Dog Trailer ---

const DT_DEFAULTS = {
   dtDollyAxleGroupMass: 23.5,
   dtTrailerAxleGroupMass: 23.5,
   dtDollyTareMass: 3.5,
};

const DT_FIELD_RULES = {
   dtDollyAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   dtTrailerAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   dtDollyTareMass: { min: 0, precision: 3, warningMax: 200 },
};

// --- B-Double Combination ---

const BD_DEFAULTS = {
   bdSteerAxleMass: 11.0,
   bdDriveAxleGroupMass: 17.5,
   bdATrailerAxleGroupMass: 23.5,
   bdBTrailerAxleGroupMass: 23.5,
   bdPmTare: 8.5,
   bdATrailerTare: 8.0,
   bdATrailerPayload: 10.0,
};

const BD_FIELD_RULES = {
   bdSteerAxleMass: { min: 0, precision: 3, warningMax: 200 },
   bdDriveAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   bdATrailerAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   bdBTrailerAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   bdPmTare: { min: 0, precision: 3, warningMax: 200 },
   bdATrailerTare: { min: 0, precision: 3, warningMax: 200 },
   bdATrailerPayload: { min: 0, precision: 3, warningMax: 500 },
};

// --- Low Loader Combination ---

const LL_DEFAULTS = {
   llSteerAxleMass: 6.5,
   llDriveAxleGroupMass: 17.5,
   llDollyAxleGroupMass: 17.5,
   llTrailerAxleGroupMass: 23.5,
   llPmTare: 8.5,
   llDollyTare: 5.0,
};

const LL_FIELD_RULES = {
   llSteerAxleMass: { min: 0, precision: 3, warningMax: 200 },
   llDriveAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   llDollyAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   llTrailerAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   llPmTare: { min: 0, precision: 3, warningMax: 200 },
   llDollyTare: { min: 0, precision: 3, warningMax: 200 },
};

// --- Road Train Combination (not used )---

const RT_DEFAULTS = {
   rtSteerAxleMass: 6.0,
   rtDriveAxleGroupMass: 15.0,
   rtTrailer1AxleGroupMass: 12.0,
   rtDolly1AxleGroupMass: 7.0,
   rtTrailer2AxleGroupMass: 12.0,
   rtDolly2AxleGroupMass: 7.0,
   rtTrailer3AxleGroupMass: 12.0,
   rtPmTare: 8.5,
   rtTrailer1Tare: 7.0,
   rtTrailer2Tare: 7.0,
};

const RT_FIELD_RULES = {
   rtSteerAxleMass: { min: 0, precision: 3, warningMax: 200 },
   rtDriveAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   rtTrailer1AxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   rtDolly1AxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   rtTrailer2AxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   rtDolly2AxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   rtTrailer3AxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
   rtPmTare: { min: 0, precision: 3, warningMax: 200 },
   rtTrailer1Tare: { min: 0, precision: 3, warningMax: 200 },
   rtTrailer2Tare: { min: 0, precision: 3, warningMax: 200 },
};

// --- Variant registry ────────────────────────────────────────────────────────
// One entry per calculator variant. To add a new variant:
//   1. Define *_DEFAULTS, *_FIELD_RULES, and a calculate* function below.
//   2. Add a row here — the $(function) loop handles init, events, and
//      blur-normalisation automatically.
//
// All variants use a consistent variant-prefix naming convention
// (e.g. `stSteerAxleMass`, `rtSteerAxleMass`, `bdSteerAxleMass`, `llSteerAxleMass`).
// ─────────────────────────────────────────────────────────────────────────────

const VARIANTS = [
   { panelId: 'atm-semi-trailer', defaults: ST_DEFAULTS, rules: ST_FIELD_RULES, calculate: calculateSemiTrailer },
   { panelId: 'atm-road-train', defaults: RT_DEFAULTS, rules: RT_FIELD_RULES, calculate: calculateRoadTrain },
   { panelId: 'atm-b-double', defaults: BD_DEFAULTS, rules: BD_FIELD_RULES, calculate: calculateBDouble },
   { panelId: 'atm-low-loader', defaults: LL_DEFAULTS, rules: LL_FIELD_RULES, calculate: calculateLowLoader },
   { panelId: 'atm-dog-trailer', defaults: DT_DEFAULTS, rules: DT_FIELD_RULES, calculate: calculateDogTrailer },
];

// --- Page load and event handling ---

$(function () {
   const $page = $('#pdf-calcs-content');

   if ($page.length === 0) return;

   for (const variant of VARIANTS) {
      const $panel = $page.find(`#${variant.panelId}`);

      // Apply default values to all fields in this variant's panel
      for (const [fieldId, value] of Object.entries(variant.defaults)) {
         $panel.find(`[data-field="${fieldId}"]`).val(value);
      }

      // Run initial calculation with default values on page load
      variant.calculate($page);

      // Recalculate whenever any field value changes
      $panel.on('input change', '[data-field]', function () {
         variant.calculate($page);
      });

      // On blur: normalise to min floor and fixed decimal precision
      $panel.on('blur', '[data-field]', function () {
         const fieldId = $(this).data('field');
         const rule = variant.rules[fieldId];
         if (!rule) return;
         const val = normalizeValue(readNumeric($(this).val()), rule);
         $(this).val(formatFieldValue(val, rule));
      });
   }

   // Reset active tab's variant to its defaults
   $page.find('#atm-reset-defaults').on('click', function () {
      const activeVariant = VARIANTS.find((v) => $page.find(`#${v.panelId}`).hasClass('active'));
      if (!activeVariant) return;
      const $panel = $page.find(`#${activeVariant.panelId}`);
      for (const [fieldId, value] of Object.entries(activeVariant.defaults)) {
         $panel.find(`[data-field="${fieldId}"]`).val(value);
      }
      activeVariant.calculate($page);
   });
});

// --- Semi Trailer: calculation and UI update ---

function calculateSemiTrailer($page) {
   const $panel = $page.find('#atm-semi-trailer');
   const inputs = {
      steerAxleMass: readNumeric($panel.find('[data-field="stSteerAxleMass"]').val()),
      driveAxleGroupMass: readNumeric($panel.find('[data-field="stDriveAxleGroupMass"]').val()),
      trailerAxleGroupMass: readNumeric($panel.find('[data-field="stTrailerAxleGroupMass"]').val()),
      primeMoverTare: readNumeric($panel.find('[data-field="stPrimeMoverTare"]').val()),
   };

   const result = calculateAtmSemiTrailer(inputs);

   $panel.find('#result-st-atm').text(formatResult(result.minimumRequiredTrailerAtm));
}

// --- Road Train Combination: calculation and UI update ---

function calculateRoadTrain($page) {
   const $panel = $page.find('#atm-road-train');
   const inputs = {
      steerAxleMass: readNumeric($panel.find('[data-field="rtSteerAxleMass"]').val()),
      driveAxleGroupMass: readNumeric($panel.find('[data-field="rtDriveAxleGroupMass"]').val()),
      trailer1AxleGroupMass: readNumeric($panel.find('[data-field="rtTrailer1AxleGroupMass"]').val()),
      dolly1AxleGroupMass: readNumeric($panel.find('[data-field="rtDolly1AxleGroupMass"]').val()),
      trailer2AxleGroupMass: readNumeric($panel.find('[data-field="rtTrailer2AxleGroupMass"]').val()),
      dolly2AxleGroupMass: readNumeric($panel.find('[data-field="rtDolly2AxleGroupMass"]').val()),
      trailer3AxleGroupMass: readNumeric($panel.find('[data-field="rtTrailer3AxleGroupMass"]').val()),
      pmTare: readNumeric($panel.find('[data-field="rtPmTare"]').val()),
      trailer1Tare: readNumeric($panel.find('[data-field="rtTrailer1Tare"]').val()),
      trailer2Tare: readNumeric($panel.find('[data-field="rtTrailer2Tare"]').val()),
   };

   const result = calculateAtmRoadTrain(inputs);

   $panel.find('#result-rt-tatm').text(formatResult(result.minimumRequired1stTrailerAtm));
   $panel.find('#result-rt-datm').text(formatResult(result.minimumRequired1stDollyAtm));
   $panel.find('#result-rt-tatm2').text(formatResult(result.minimumRequired2ndTrailerAtm));
   $panel.find('#result-rt-datm2').text(formatResult(result.minimumRequired2ndDollyAtm));
   $panel.find('#result-rt-tatm3').text(formatResult(result.minimumRequired3rdTrailerAtm));

   const dogTrailer1Atm = result.minimumRequired1stDollyAtm + result.minimumRequired2ndTrailerAtm;
   const dogTrailer2Atm = result.minimumRequired2ndDollyAtm + result.minimumRequired3rdTrailerAtm;
   $panel.find('#result-rt-dtatm1').text(formatResult(dogTrailer1Atm));
   $panel.find('#result-rt-dtatm2').text(formatResult(dogTrailer2Atm));
}

// --- B-Double Combination: calculation and UI update ---

function calculateBDouble($page) {
   const $panel = $page.find('#atm-b-double');
   const inputs = {
      steerAxleMass: readNumeric($panel.find('[data-field="bdSteerAxleMass"]').val()),
      driveAxleGroupMass: readNumeric($panel.find('[data-field="bdDriveAxleGroupMass"]').val()),
      aTrailerAxleGroupMass: readNumeric($panel.find('[data-field="bdATrailerAxleGroupMass"]').val()),
      bTrailerAxleGroupMass: readNumeric($panel.find('[data-field="bdBTrailerAxleGroupMass"]').val()),
      pmTare: readNumeric($panel.find('[data-field="bdPmTare"]').val()),
      aTrailerTare: readNumeric($panel.find('[data-field="bdATrailerTare"]').val()),
      aTrailerPayload: readNumeric($panel.find('[data-field="bdATrailerPayload"]').val()),
   };

   const result = calculateAtmBDouble(inputs);

   $panel.find('#result-bd-atma').text(formatResult(result.minimumRequiredATrailerAtm));
   $panel.find('#result-bd-atmb').text(formatResult(result.minimumRequiredBTrailerAtm));
}

// --- Low Loader Combination: calculation and UI update ---

function calculateLowLoader($page) {
   const $panel = $page.find('#atm-low-loader');
   const inputs = {
      steerAxleMass: readNumeric($panel.find('[data-field="llSteerAxleMass"]').val()),
      driveAxleGroupMass: readNumeric($panel.find('[data-field="llDriveAxleGroupMass"]').val()),
      dollyAxleGroupMass: readNumeric($panel.find('[data-field="llDollyAxleGroupMass"]').val()),
      trailerAxleGroupMass: readNumeric($panel.find('[data-field="llTrailerAxleGroupMass"]').val()),
      pmTare: readNumeric($panel.find('[data-field="llPmTare"]').val()),
      dollyTare: readNumeric($panel.find('[data-field="llDollyTare"]').val()),
   };

   const result = calculateAtmLowLoader(inputs);

   $panel.find('#result-ll-datm').text(formatResult(result.minimumRequiredDollyAtm));
   $panel.find('#result-ll-tatm').text(formatResult(result.minimumRequiredTrailerAtm));
}

// --- Dog Trailer: calculation and UI update ---

function calculateDogTrailer($page) {
   const $panel = $page.find('#atm-dog-trailer');
   const inputs = {
      dollyAxleGroupMass: readNumeric($panel.find('[data-field="dtDollyAxleGroupMass"]').val()),
      trailerAxleGroupMass: readNumeric($panel.find('[data-field="dtTrailerAxleGroupMass"]').val()),
      dollyTareMass: readNumeric($panel.find('[data-field="dtDollyTareMass"]').val()),
   };

   const result = calculateAtmDogTrailer(inputs);

   $panel.find('#result-dt-datm').text(result.ok ? formatResult(result.dollyATM) : '\u2014');
   $panel.find('#result-dt-tatm').text(result.ok ? formatResult(result.trailerATM) : '\u2014');
   $panel.find('#result-dt-dtatm').text(result.ok ? formatResult(result.dogTrailerATM) : '\u2014');
}
